import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Phone, MessageCircle, Mail, Globe, Linkedin, Download, Users, CheckCircle, AlertCircle, FileText, Target, Calendar, Eye, StopCircle } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { useInboundLeadsBySource } from '../../hooks/useSupabaseLeads';
import { supabase } from '../../lib/supabase';
import StatsCard from '../Dashboard/StatsCard';
import OverviewModal from '../LeadGen/OverviewModal';
import InboundInsightsModal from './InboundInsightsModal';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';
import LoadingScreen from '../shared/LoadingScreen';
import { mapActionToCampaignType, fetchCampaignIdByType, fetchInboundCampaignId, getSentToFlags, validateCampaignAssignment, validateContactForChannel, getAgentForCampaignType, checkInboundTemplateReadiness } from '../../utils/campaignHelpers';
import { getLeadStatusBadge } from '../../utils/leadStatusBadge';

const InboundLeadsPage: React.FC = () => {
  const { data: inboundLeads, isLoading, error } = useInboundLeadsBySource();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [outreachFilter, setOutreachFilter] = useState('all');
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [selectAllLeads, setSelectAllLeads] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [selectedOverviewLead, setSelectedOverviewLead] = useState<any>(null);
  const [activeBulkAction, setActiveBulkAction] = useState<string | null>(null);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [selectedInsightsLead, setSelectedInsightsLead] = useState<any>(null);
  const itemsPerPage = 10;

  const countryOptions = useMemo(() => {
    const countries = new Set<string>();
    inboundLeads.forEach(l => { if (l.company_country) countries.add(l.company_country); });
    return Array.from(countries).sort().map(c => ({ value: c, label: c }));
  }, [inboundLeads]);

  const industryOptions = useMemo(() => {
    const industries = new Set<string>();
    inboundLeads.forEach(l => { if (l.industry_1) industries.add(l.industry_1); });
    return Array.from(industries).sort().map(i => ({ value: i, label: i }));
  }, [inboundLeads]);

  const filteredLeads = inboundLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_email_1 || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_mobile_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
                         (lead.ovrall_lead_status || 'new').toLowerCase().includes(statusFilter.toLowerCase());
    const matchesCountry = countryFilter.length === 0 || countryFilter.includes(lead.company_country || '');
    const matchesIndustry = industryFilter.length === 0 || industryFilter.includes(lead.industry_1 || '');
    let matchesOutreach = true;
    if (outreachFilter === 'all_channels') {
      matchesOutreach = !!(lead.sent_to_ca && lead.sent_to_wa && lead.sent_to_ea && lead.sent_to_la);
    } else if (outreachFilter === 'email') {
      matchesOutreach = !!lead.sent_to_ea && !lead.sent_to_ca && !lead.sent_to_wa && !lead.sent_to_la;
    } else if (outreachFilter === 'whatsapp') {
      matchesOutreach = !!lead.sent_to_wa && !lead.sent_to_ca && !lead.sent_to_ea && !lead.sent_to_la;
    } else if (outreachFilter === 'linkedin') {
      matchesOutreach = !!lead.sent_to_la && !lead.sent_to_ca && !lead.sent_to_wa && !lead.sent_to_ea;
    } else if (outreachFilter === 'none') {
      matchesOutreach = !lead.sent_to_ca && !lead.sent_to_wa && !lead.sent_to_ea && !lead.sent_to_la;
    }
    return matchesSearch && matchesStatus && matchesCountry && matchesIndustry && matchesOutreach;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedLeads = useMemo(() => {
    return filteredLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredLeads, itemsPerPage, startIndex]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, countryFilter, industryFilter, outreachFilter]);

  const totalInboundLeads = inboundLeads.length;
  const assignedToCampaignCount = inboundLeads.filter(lead => lead.assigned_to_campaign).length;
  const engagedLeadsCount = inboundLeads.filter(lead => {
    const status = (lead.ovrall_lead_status || '').toLowerCase();
    return status === 'talking' || status === 'booked';
  }).length;
  const bookedDemosCount = inboundLeads.filter(lead => {
    const status = (lead.meeting_booked || '').toLowerCase();
    return status === 'yes' || status === 'true';
  }).length;

  const handleSelectAll = () => {
    const allFilteredIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
    if (selectedLeadIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } else {
      setSelectedLeadIds(allFilteredIds);
      setSelectAllLeads(false);
    }
  };

  const handleSelectAllLeads = () => {
    const allFilteredIds = filteredLeads.map(lead => lead.lead_id).filter(Boolean);
    setSelectedLeadIds(allFilteredIds);
    setSelectAllLeads(true);
  };

  const handleSelectLead = (lead_id: string) => {
    if (!lead_id) return;
    setSelectedLeadIds(prev => {
      if (prev.includes(lead_id)) {
        setSelectAllLeads(false);
        return prev.filter(id => id !== lead_id);
      } else {
        return [...prev, lead_id];
      }
    });
  };

  const allCurrentPageSelected = useMemo(() => {
    const allPageIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
    return allPageIds.length > 0 && allPageIds.every(id => selectedLeadIds.includes(id));
  }, [paginatedLeads, selectedLeadIds]);

  const showSelectAllBanner = allCurrentPageSelected && !selectAllLeads && filteredLeads.length > paginatedLeads.length;

  const handleBulkAction = async (action: string) => {
    if (selectedLeadIds.length === 0) return;
    setActiveBulkAction(action);

    try {
      const leadsToProcess = selectAllLeads
        ? filteredLeads.map(lead => lead.lead_id).filter(Boolean)
        : selectedLeadIds;

      if (action === 'Stop') {
        await supabase
          .from('leads_tracking')
          .update({ ovrall_lead_status: 'closed', updated_at: new Date().toISOString() })
          .in('lead_id', leadsToProcess);
        setSelectedLeadIds([]);
        setSelectAllLeads(false);
        return;
      }

      const campaignType = mapActionToCampaignType(action);
      if (!campaignType) {
        setActiveBulkAction(null);
        return;
      }

      const validation = await validateCampaignAssignment(leadsToProcess, campaignType);
      let leadsToAssign = leadsToProcess;

      if (!validation.isValid) {
        if (validation.conflictingCampaigns && validation.conflictingCampaigns.length > 0) {
          const conflictList = validation.conflictingCampaigns
            .map(c => `\u2022 ${c.companyName} (${c.type})`)
            .join('\n');
          alert(`Cannot assign some leads:\n\n${conflictList}\n\nThese leads are already assigned or have conflicts.`);

          const conflictingLeadIdsSet = new Set(validation.conflictingLeadIds || []);
          const validLeadIds = leadsToProcess.filter(id => !conflictingLeadIdsSet.has(id));

          if (validLeadIds.length === 0) {
            setActiveBulkAction(null);
            return;
          }

          const proceed = confirm(
            `${validLeadIds.length} out of ${leadsToProcess.length} leads can be assigned. Continue?`
          );
          if (!proceed) {
            setActiveBulkAction(null);
            return;
          }
          leadsToAssign = validLeadIds;
        } else {
          alert(validation.error || 'Cannot assign to this campaign');
          setActiveBulkAction(null);
          return;
        }
      }

      const leadsWithContact = leadsToAssign.map(leadId => {
        const lead = inboundLeads.find(l => l.lead_id === leadId);
        return {
          lead_id: leadId,
          company_name: lead?.company_name,
          dm_mobile_1: lead?.dm_mobile_1,
          dm_email_1: lead?.dm_email_1,
          dm_linkedin: lead?.dm_linkedin,
        };
      });

      const contactValidation = validateContactForChannel(leadsWithContact, campaignType);

      if (contactValidation.missingContactLeads.length > 0) {
        const missingList = contactValidation.missingContactLeads
          .slice(0, 10)
          .map(c => `\u2022 ${c.companyName} - Missing ${c.missingField}`)
          .join('\n');
        const suffix = contactValidation.missingContactLeads.length > 10
          ? `\n...and ${contactValidation.missingContactLeads.length - 10} more`
          : '';

        alert(`Cannot assign ${contactValidation.missingContactLeads.length} lead(s) - missing required contact details:\n\n${missingList}${suffix}`);

        if (contactValidation.validLeadIds.length === 0) {
          setActiveBulkAction(null);
          return;
        }

        const proceed = confirm(
          `${contactValidation.validLeadIds.length} out of ${leadsToAssign.length} leads have the required contact details. Continue with those?`
        );
        if (!proceed) {
          setActiveBulkAction(null);
          return;
        }
        leadsToAssign = contactValidation.validLeadIds;
      }

      const firstLead = inboundLeads.find(l => l.lead_id === leadsToAssign[0]);

      const urgentLeadIds: string[] = [];
      const nonUrgentLeadIds: string[] = [];
      for (const lid of leadsToAssign) {
        const lead = inboundLeads.find(l => l.lead_id === lid);
        if (lead?.inbound_type === 'non_urgent') {
          nonUrgentLeadIds.push(lid);
        } else {
          urgentLeadIds.push(lid);
        }
      }

      const clientIdForGate = firstLead?.client_id;
      if (clientIdForGate) {
        const { urgentReady, nonUrgentReady } = await checkInboundTemplateReadiness(clientIdForGate);
        const blockedMessages: string[] = [];
        if (urgentLeadIds.length > 0 && !urgentReady) {
          blockedMessages.push(`\u2022 ${urgentLeadIds.length} urgent lead(s) - "Inbound Inquiry" email templates are not approved`);
        }
        if (nonUrgentLeadIds.length > 0 && !nonUrgentReady) {
          blockedMessages.push(`\u2022 ${nonUrgentLeadIds.length} non-urgent lead(s) - "Inbound Lead Magnet" email templates are not approved`);
        }
        if (blockedMessages.length > 0) {
          if (!urgentReady) urgentLeadIds.length = 0;
          if (!nonUrgentReady) nonUrgentLeadIds.length = 0;
          const remaining = [...urgentLeadIds, ...nonUrgentLeadIds];
          if (remaining.length === 0) {
            alert(`Cannot assign to Inbound campaign:\n\n${blockedMessages.join('\n')}\n\nApprove the required email templates in Email Agent settings first.`);
            setActiveBulkAction(null);
            return;
          }
          const proceed = confirm(
            `Some leads cannot be assigned:\n\n${blockedMessages.join('\n')}\n\n${remaining.length} lead(s) can still be assigned. Continue with those?`
          );
          if (!proceed) {
            setActiveBulkAction(null);
            return;
          }
          leadsToAssign = remaining;
        }
      }

      const urgentCampaignId = urgentLeadIds.length > 0
        ? await fetchInboundCampaignId('urgent', firstLead?.user_id)
        : null;
      const nonUrgentCampaignId = nonUrgentLeadIds.length > 0
        ? await fetchInboundCampaignId('non_urgent', firstLead?.user_id)
        : null;

      if ((urgentLeadIds.length > 0 && !urgentCampaignId) || (nonUrgentLeadIds.length > 0 && !nonUrgentCampaignId)) {
        alert(`No active Inbound campaign found. Please create a campaign first.`);
        setActiveBulkAction(null);
        return;
      }

      const sentToFlags = getSentToFlags(action);
      const trackingUpdates: any = {
        assigned_to_campaign: true,
        sent_for_outreach: true,
        ovrall_lead_status: 'assigned',
        ...sentToFlags,
        updated_at: new Date().toISOString()
      };

      await supabase
        .from('leads_tracking')
        .update(trackingUpdates)
        .in('lead_id', leadsToAssign);

      const agentName = getAgentForCampaignType(campaignType);
      const junctionRecords = leadsToAssign.map(leadId => {
        const lead = inboundLeads.find(l => l.lead_id === leadId);
        const isNonUrgent = lead?.inbound_type === 'non_urgent';
        return {
          lead_id: leadId,
          campaign_id: isNonUrgent ? nonUrgentCampaignId : urgentCampaignId,
          campaign_type: campaignType,
          campaign_step: 0,
          status: 'assigned',
          client_id: lead?.client_id,
          user_id: lead?.user_id,
          agent: agentName
        };
      });

      const { error: junctionError } = await supabase
        .from('leads_campaigns')
        .insert(junctionRecords);

      if (junctionError) {
        alert('Failed to assign some leads to campaign');
        setActiveBulkAction(null);
        return;
      }

      setSelectedLeadIds([]);
      setSelectAllLeads(false);
    } catch (err) {
      console.error('Failed to perform bulk action:', err);
    } finally {
      setActiveBulkAction(null);
    }
  };

  const handleExportCSV = () => {
    if (selectedLeadIds.length === 0) return;
    setActiveBulkAction('Export');

    try {
      const selectedLeads = selectAllLeads
        ? filteredLeads
        : filteredLeads.filter(lead => selectedLeadIds.includes(lead.lead_id));

      const csvHeaders = [
        'Company Name', 'Country', 'Company Phone', 'Company Website', 'Company LinkedIn',
        'Industry', 'Person Name', 'Person Title', 'Person Mobile',
        'Person Email', 'Person LinkedIn', 'Lead Status'
      ];

      const csvRows = selectedLeads.map(lead => [
        lead.company_name || '',
        lead.company_country || '',
        lead.company_phone || '',
        lead.company_website || '',
        lead.company_linkedin || '',
        lead.industry_1 || '',
        lead.dm_full_name || '',
        lead.dm_title || '',
        lead.dm_mobile_1 || '',
        lead.dm_email_1 || '',
        lead.dm_linkedin || '',
        lead.ovrall_lead_status || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${field.toString().replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inbound-leads-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setActiveBulkAction(null);
    }
  };

  const handleOpenInsightsModal = (lead: any) => {
    setSelectedInsightsLead(lead);
    setShowInsightsModal(true);
  };

  const handleCloseInsightsModal = () => {
    setShowInsightsModal(false);
    setSelectedInsightsLead(null);
  };

  const handleOpenOverviewModal = (lead: any) => {
    setSelectedOverviewLead(lead);
    setShowOverviewModal(true);
  };

  const handleCloseOverviewModal = () => {
    setShowOverviewModal(false);
    setSelectedOverviewLead(null);
  };

  if (isLoading && inboundLeads.length === 0) {
    return <LoadingScreen />;
  }

  const getLeadStatusTag = (status: string) => getLeadStatusBadge(status, 'New');

  const getSentForOutreachIcons = (lead: any) => {
    return (
      <div className="flex flex-col items-center space-y-2">
        <Phone
          size={16}
          className={`transition-all duration-300 ${
            lead.sent_to_ca
              ? 'text-[#1875FF] drop-shadow-[0_0_8px_rgba(24,117,255,0.6)]'
              : 'text-gray-300'
          }`}
          title="Call Agent"
        />
        <MessageCircle
          size={16}
          className={`transition-all duration-300 ${
            lead.sent_to_wa
              ? 'text-[#05E0D1] drop-shadow-[0_0_8px_rgba(5,224,209,0.6)]'
              : 'text-gray-300'
          }`}
          title="WhatsApp Agent"
        />
        <Mail
          size={16}
          className={`transition-all duration-300 ${
            lead.sent_to_ea
              ? 'text-[#1875FF] drop-shadow-[0_0_8px_rgba(24,117,255,0.5)]'
              : 'text-gray-300'
          }`}
          title="Email Agent"
        />
        <Linkedin
          size={16}
          className={`transition-all duration-300 ${
            lead.sent_to_la
              ? 'text-[#05E0D1] drop-shadow-[0_0_8px_rgba(5,224,209,0.6)]'
              : 'text-gray-300'
          }`}
          title="LinkedIn Agent"
        />
      </div>
    );
  };

  const renderBulkActions = () => {
    if (selectedLeadIds.length === 0) return null;

    return (
      <div className="bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border-2 border-[#1875FF]/40 rounded-xl p-5 shadow-2xl shadow-[#1875FF]/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-lg flex items-center justify-center animate-pulse shadow-md">
              <CheckCircle className="text-[#1875FF]" size={20} />
            </div>
            <div className="flex flex-col space-y-1">
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1]">
                {selectAllLeads ? `All ${filteredLeads.length}` : selectedLeadIds.length} lead{(selectAllLeads ? filteredLeads.length : selectedLeadIds.length) > 1 ? 's' : ''} selected
              </span>
              {showSelectAllBanner && (
                <button
                  onClick={handleSelectAllLeads}
                  className="text-[#1875FF] font-semibold hover:underline focus:outline-none text-sm text-left"
                >
                  Select all {filteredLeads.length} leads
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleBulkAction('Inbound')}
              disabled={activeBulkAction !== null}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {activeBulkAction === 'Inbound' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Assigning...</span>
                </>
              ) : (
                <>
                  <Globe size={18} />
                  <span>Inbound Campaign</span>
                </>
              )}
            </button>
            <button
              onClick={() => handleBulkAction('Stop')}
              disabled={activeBulkAction !== null}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#05E0D1]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {activeBulkAction === 'Stop' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Closing...</span>
                </>
              ) : (
                <>
                  <StopCircle size={18} />
                  <span>Stop & Close</span>
                </>
              )}
            </button>
            <button
              onClick={handleExportCSV}
              disabled={activeBulkAction !== null}
              className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#1875FF]/40 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {activeBulkAction === 'Export' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <Download size={18} />
                  <span>Export CSV</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 mb-6 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Inbound Leads"
          value={totalInboundLeads}
          icon={Target}
          color="blue"
        />
        <StatsCard
          title="Assigned to Campaign"
          value={assignedToCampaignCount}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Engaged Leads"
          value={engagedLeadsCount}
          icon={Users}
          color="orange"
        />
        <StatsCard
          title="Booked Demos"
          value={bookedDemosCount}
          icon={Calendar}
          color="green"
        />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search inbound leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-y-3">
            <Filter size={16} className="text-gray-500" />
            <SimpleCustomSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'new', label: 'New' },
                { value: 'talking', label: 'Talking' },
                { value: 'follow up', label: 'Follow Up' },
                { value: 'booked', label: 'Booked' },
                { value: 'closed', label: 'Closed' }
              ]}
              placeholder="All Status"
              className="w-44"
            />
            <MultiSelectDropdown
              value={countryFilter}
              onChange={setCountryFilter}
              options={countryOptions}
              placeholder="Country"
              className="w-44"
            />
            <MultiSelectDropdown
              value={industryFilter}
              onChange={setIndustryFilter}
              options={industryOptions}
              placeholder="Industry"
              className="w-44"
            />
            <SimpleCustomSelect
              value={outreachFilter}
              onChange={setOutreachFilter}
              options={[
                { value: 'all', label: 'All Outreach' },
                { value: 'all_channels', label: 'All Channels' },
                { value: 'email', label: 'Email Only' },
                { value: 'whatsapp', label: 'WhatsApp Only' },
                { value: 'linkedin', label: 'LinkedIn Only' },
                { value: 'none', label: 'No Outreach Yet' }
              ]}
              placeholder="All Outreach"
              className="w-44"
            />
          </div>
        </div>
      </div>

      {renderBulkActions()}

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table style={{ tableLayout: 'fixed' }} className="w-full">
            <colgroup>
              <col style={{ width: '36px' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '60px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '80px' }} />
            </colgroup>
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-center px-1 py-4">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={paginatedLeads.length > 0 && selectedLeadIds.length === paginatedLeads.map(lead => lead.lead_id).filter(Boolean).length}
                      ref={(el) => {
                        if (el) {
                          const allFilteredIds = paginatedLeads.map(lead => lead.lead_id).filter(Boolean);
                          el.indeterminate = selectedLeadIds.length > 0 && selectedLeadIds.length < allFilteredIds.length;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                    />
                  </div>
                </th>
                <th className="text-left px-3 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Person
                </th>
                <th className="text-left px-3 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Contacts
                </th>
                <th className="text-center px-1 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Insights
                </th>
                <th className="text-center px-1 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Lead Status
                </th>
                <th className="text-center px-1 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans text-[10px]">
                  Outreach
                </th>
                <th className="text-center px-1 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans text-[10px]">
                  Overview
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {paginatedLeads.map((lead) => (
                <tr key={lead.lead_id} className="hover:bg-[#1875FF]/5 transition-colors">
                  <td className="px-1 py-4">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={lead.lead_id ? selectedLeadIds.includes(lead.lead_id) : false}
                        onChange={() => handleSelectLead(lead.lead_id)}
                        className="custom-checkbox w-3.5 h-3.5 rounded border border-gray-300 bg-white hover:border-[#1875FF]/50 focus:ring-0 transition-all duration-200 cursor-pointer checked:bg-gradient-to-r checked:from-[#1875FF] checked:to-[#05E0D1] checked:border-transparent"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-4 overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      <div className="font-medium text-gray-900 truncate">{lead.dm_full_name || ''}</div>
                      {lead.inbound_type && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded flex-shrink-0 ${
                          lead.inbound_type === 'urgent'
                            ? 'bg-red-50 text-red-600 border border-red-200'
                            : 'bg-blue-50 text-blue-600 border border-blue-200'
                        }`}>
                          {lead.inbound_type === 'urgent' ? 'Urgent' : 'Non-Urgent'}
                        </span>
                      )}
                    </div>
                    {lead.dm_title && (
                      <div className="text-sm text-gray-500 truncate">{lead.dm_title}</div>
                    )}
                    {lead.company_name && (
                      <div className="flex items-center space-x-1.5 mt-0.5">
                        <span className="text-sm text-gray-700 truncate">{lead.company_name}</span>
                        {lead.company_website && (
                          <a
                            href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1875FF] hover:text-[#1875FF]/80 transition-colors flex-shrink-0"
                            title="Company Website"
                          >
                            <Globe size={13} />
                          </a>
                        )}
                        {lead.company_linkedin && (
                          <a
                            href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1875FF] hover:text-[#1875FF]/80 transition-colors flex-shrink-0"
                            title="Company LinkedIn"
                          >
                            <Linkedin size={13} />
                          </a>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4 overflow-hidden">
                    <div className="flex flex-col space-y-1.5">
                      {lead.dm_mobile_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                          <Phone size={13} className="text-[#1875FF] flex-shrink-0" />
                          <span className="truncate">{lead.dm_mobile_1}</span>
                        </div>
                      )}
                      {lead.dm_email_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                          <Mail size={13} className="text-[#05E0D1] flex-shrink-0" />
                          <span className="truncate">{lead.dm_email_1}</span>
                        </div>
                      )}
                      {lead.dm_linkedin && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                          <Linkedin size={13} className="text-[#1875FF] flex-shrink-0" />
                          <a
                            href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1875FF] hover:underline truncate"
                          >
                            Profile
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-4 text-center">
                    <button
                      onClick={() => handleOpenInsightsModal(lead)}
                      className="group relative flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 hover:from-[#1875FF]/20 hover:to-[#05E0D1]/20 border-2 border-[#1875FF]/20 hover:border-[#1875FF]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#1875FF]/20 hover:scale-105 mx-auto"
                      title="View Insights"
                    >
                      <Eye size={16} className="text-[#1875FF]" />
                    </button>
                  </td>
                  <td className="px-1 py-4 text-center">
                    {getLeadStatusTag(lead.ovrall_lead_status)}
                  </td>
                  <td className="px-1 py-4 text-center">
                    {getSentForOutreachIcons(lead)}
                  </td>
                  <td className="px-1 py-4 text-center">
                    <button
                      onClick={() => handleOpenOverviewModal(lead)}
                      className="group relative flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 hover:from-[#1875FF]/20 hover:to-[#05E0D1]/20 border-2 border-[#1875FF]/20 hover:border-[#1875FF]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#1875FF]/20 hover:scale-105 mx-auto"
                      title="View Overview"
                    >
                      <FileText size={18} className="text-[#1875FF]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLeads.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-extrabold font-display">No inbound leads found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            {searchTerm ? 'Try adjusting your search' : 'Inbound leads will appear here once they are received'}
          </div>
        </div>
      )}

      {renderBulkActions()}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredLeads.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {showInsightsModal && selectedInsightsLead && (
        <InboundInsightsModal
          lead={selectedInsightsLead}
          onClose={handleCloseInsightsModal}
        />
      )}

      {showOverviewModal && selectedOverviewLead && (
        <OverviewModal
          lead={selectedOverviewLead}
          onClose={handleCloseOverviewModal}
        />
      )}
    </div>
  );
};

export default InboundLeadsPage;
